import React, { useState } from 'react';
import {
  Card,
  FormLayout,
  Select,
  Button,
  BlockStack,
  InlineStack,
  TextContainer,
  Badge,
  Banner,
  Loading,
  TextField,
  Checkbox,
  Modal,
} from '@shopify/polaris';
import { MagicMinor } from '@shopify/polaris-icons';

interface Product {
  id: string;
  name: string;
  price: number;
  description?: string;
}

interface AIMessageGeneratorProps {
  products: Product[];
  customerPhone?: string;
  onMessageGenerated: (message: any, type: string) => void;
}

export const AIMessageGenerator: React.FC<AIMessageGeneratorProps> = ({
  products,
  customerPhone,
  onMessageGenerated,
}) => {
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [tone, setTone] = useState<'professional' | 'casual' | 'enthusiastic'>('professional');
  const [targetAudience, setTargetAudience] = useState('');
  const [includeOffers, setIncludeOffers] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatedMessage, setGeneratedMessage] = useState<any>(null);
  const [messageType, setMessageType] = useState<string>('');
  const [reasoning, setReasoning] = useState('');
  const [previewModalActive, setPreviewModalActive] = useState(false);
  const [error, setError] = useState('');

  const toneOptions = [
    { label: 'Professional', value: 'professional' },
    { label: 'Casual', value: 'casual' },
    { label: 'Enthusiastic', value: 'enthusiastic' },
  ];

  const productOptions = products.map(p => ({
    label: `${p.name} - $${p.price}`,
    value: p.id,
  }));

  const handleGenerate = async () => {
    if (selectedProducts.length === 0) {
      setError('Please select at least one product');
      return;
    }

    setError('');
    setGenerating(true);

    try {
      const response = await fetch('/api/ai-interactive/generate-message-from-products', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          productIds: selectedProducts,
          tone,
          targetAudience: targetAudience || undefined,
          includeOffers,
          customerPhone,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate message');
      }

      const data = await response.json();

      setGeneratedMessage(data.data.message);
      setMessageType(data.data.suggestedType);
      setReasoning(data.data.reasoning);
      setPreviewModalActive(true);
    } catch (err: any) {
      setError(err.message || 'Failed to generate message');
    } finally {
      setGenerating(false);
    }
  };

  const handleUseMessage = () => {
    if (generatedMessage && messageType) {
      onMessageGenerated(generatedMessage, messageType);
      setPreviewModalActive(false);
      // Reset form
      setSelectedProducts([]);
      setTargetAudience('');
      setIncludeOffers(false);
      setGeneratedMessage(null);
    }
  };

  const renderPreview = () => {
    if (!generatedMessage) return null;

    return (
      <div style={{
        padding: '1rem',
        background: '#f4f6f8',
        borderRadius: '8px',
        maxWidth: '350px',
        margin: '1rem 0'
      }}>
        {generatedMessage.header && (
          <p style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
            {generatedMessage.header}
          </p>
        )}
        <p style={{ marginBottom: '0.5rem' }}>
          {generatedMessage.body || 'Your message will appear here...'}
        </p>
        {generatedMessage.footer && (
          <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
            {generatedMessage.footer}
          </p>
        )}

        {messageType === 'buttons' && generatedMessage.buttons && (
          <div style={{ marginTop: '1rem' }}>
            <BlockStack gap="200">
              {generatedMessage.buttons.map((btn: any, i: number) => (
                <div key={i} style={{
                  padding: '0.5rem',
                  background: 'white',
                  borderRadius: '4px',
                  textAlign: 'center',
                  border: '1px solid #ddd'
                }}>
                  {btn.title}
                </div>
              ))}
            </BlockStack>
          </div>
        )}

        {messageType === 'list' && generatedMessage.buttonText && (
          <div style={{ marginTop: '1rem' }}>
            <div style={{
              padding: '0.75rem',
              background: 'white',
              borderRadius: '4px',
              textAlign: 'center',
              border: '1px solid #ddd'
            }}>
              📋 {generatedMessage.buttonText}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Card>
        <BlockStack gap="500">
          <TextContainer>
            <InlineStack gap="200">
              <div style={{ marginRight: '0.5rem' }}>
                <Badge tone="info">
                  AI-Powered
                </Badge>
              </div>
              <p style={{ fontWeight: 'bold' }}>Auto-Generate Interactive Messages</p>
            </InlineStack>
            <p style={{ fontSize: '0.9rem', color: '#666' }}>
              Let AI create the perfect interactive message for your products
            </p>
          </TextContainer>

          <FormLayout>
            <Select
              label="Select Products"
              options={productOptions}
              value={selectedProducts[0] || ''}
              onChange={(value) => setSelectedProducts([value])}
              placeholder="Choose products to feature"
            />

            <Select
              label="Message Tone"
              options={toneOptions}
              value={tone}
              onChange={(value) => setTone(value as any)}
              helpText="AI will match this tone in the message"
            />

            <TextField
              label="Target Audience (optional)"
              value={targetAudience}
              onChange={setTargetAudience}
              placeholder="e.g., young professionals, parents, tech enthusiasts"
              helpText="Help AI personalize the message"
              autoComplete="off"
            />

            <Checkbox
              label="Include promotional language"
              checked={includeOffers}
              onChange={setIncludeOffers}
              helpText="Add urgency and special offer messaging"
            />

            {error && (
              <Banner tone="critical" onDismiss={() => setError('')}>
                <p>{error}</p>
              </Banner>
            )}

            <Button
              variant="primary"
              icon={MagicMinor}
              onClick={handleGenerate}
              loading={generating}
              disabled={selectedProducts.length === 0}
            >
              Generate AI Message
            </Button>
          </FormLayout>

          {generating && (
            <Banner tone="info">
              <InlineStack gap="200">
                <Loading />
                <p>AI is analyzing products and generating the perfect message...</p>
              </InlineStack>
            </Banner>
          )}
        </BlockStack>
      </Card>

      {/* Preview Modal */}
      <Modal
        open={previewModalActive}
        onClose={() => setPreviewModalActive(false)}
        title="AI-Generated Message Preview"
        primaryAction={{
          content: 'Use This Message',
          onAction: handleUseMessage,
        }}
        secondaryActions={[
          {
            content: 'Regenerate',
            onAction: handleGenerate,
          },
          {
            content: 'Cancel',
            onAction: () => setPreviewModalActive(false),
          },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="500">
            <Banner tone="success">
              <BlockStack gap="200">
                <p><strong>Message Type:</strong> {messageType}</p>
                <p><strong>AI Reasoning:</strong> {reasoning}</p>
              </BlockStack>
            </Banner>

            <TextContainer>
              <p style={{ fontWeight: 'bold' }}>Preview:</p>
            </TextContainer>

            {renderPreview()}

            <Banner tone="info">
              <p>
                This message was generated using AI based on your product data and preferences.
                You can use it as-is or regenerate for a different version.
              </p>
            </Banner>
          </BlockStack>
        </Modal.Section>
      </Modal>
    </>
  );
};
